import { Component, HostBinding, OnDestroy, OnInit } from '@angular/core'
import { UntypedFormBuilder, UntypedFormControl } from '@angular/forms'
import { Contact } from '@app/models/contact'
import { CcwDiscountItem } from '@app/models/discount-item.model'
import { PaymentFrequency } from '@app/models/payment-frequency'
import { Pet } from '@app/models/pet.model'
import { Quote } from '@app/models/quote.model'
import { PetState, PetStateModel } from '@app/shared/store/pet/pet.state'
import { QuoteFlowAction } from '@app/shared/store/quote-flow/quote-flow.actions'
import { QuoteSessionAction } from '@app/shared/store/quote-session/quote-session.actions'
import {
	QuoteSessionState,
	QuoteSessionStateModel,
} from '@app/shared/store/quote-session/quote-session.state'
import { Select, Store } from '@ngxs/store'
import { SettingsState } from '@app/shared/store/settings/settings.state'
import { firstValueFrom, map, min, Observable, Subject, takeUntil } from 'rxjs'

@Component({
	selector: 'app-quote-breakdown',
	templateUrl: './quote-breakdown.component.html',
	styleUrls: ['./quote-breakdown.component.scss'],
})
export class QuoteBreakdownComponent implements OnInit, OnDestroy {
	@Select(QuoteSessionState.contactState)
	public contact$!: Observable<Contact>
	@Select(QuoteSessionState.quoteState)
	public quote$!: Observable<Quote>
	@Select(QuoteSessionState)
	public quoteSession$!: Observable<QuoteSessionStateModel>
	@Select(PetState.activePets)
	public pets$!: Observable<Pet[]>
	@Select(SettingsState.allowStartDatePast30Days)
	allowStartDatePast30Days$!: Observable<boolean>

	public contactFullName$: Observable<string>
	public activePetsOnQuote$: Observable<Pet[]>
	public totalDiscountPercentage$: Observable<number>
	public paymentFrequency$: Observable<string>
	public discountDisplayList$: Observable<CcwDiscountItem[]>
	public quoteStarted$: Observable<boolean>
	public policyStartDate: UntypedFormControl = new UntypedFormControl(null)
	public paymentFrequency: UntypedFormControl = new UntypedFormControl(PaymentFrequency.Monthly)
	public displayQuoteData: boolean = true
	public emptySvgTemplate: string = ''
	public PaymentFrequency = PaymentFrequency
	public minDate!: Date
	public maxDate!: Date

	#ngUnsubscribe$: Subject<void> = new Subject()

	@HostBinding('class')
	public get klassList(): string {
		return !this.displayQuoteData ? 'm--full-height' : ''
	}

	constructor(private readonly fb: UntypedFormBuilder, private readonly store: Store) {
		this.allowStartDatePast30Days$.pipe(takeUntil(this.#ngUnsubscribe$)).subscribe(async (disableLimit) => { 
			if(!disableLimit) {
				this.#setDateLimits()
		}});

		this.quoteStarted$ = this.quoteSession$.pipe(map((qs) => !!qs.quoteNumber))

		this.quote$.pipe(takeUntil(this.#ngUnsubscribe$)).subscribe((q) => {
			if (!q) return
			this.policyStartDate.patchValue(q.policyStartDate, { emitEvent: false })
			this.paymentFrequency.patchValue(q.paymentModality, { emitEvent: false })
		})

		this.contactFullName$ = this.contact$.pipe(
			map((c) => `${c?.firstName ?? 'Pet'} ${c?.lastName ?? 'Parent'}`)
		)

		this.activePetsOnQuote$ = this.quoteSession$.pipe(
			map((s) => s.pets?.filter((p) => !p.removed) ?? [])
		)

		this.totalDiscountPercentage$ = this.quote$.pipe(
			map((quote) => {
				const totalDiscountPercentage = quote?.totalDiscountPercentage || 0

				return totalDiscountPercentage > 25 ? 25 : totalDiscountPercentage
			})
		)

		this.paymentFrequency$ = this.quote$.pipe(map((quote) => quote?.paymentModality || 'Monthly'))

		this.discountDisplayList$ = this.quoteSession$.pipe(
			map((quote) => quote?.ccwDiscountsDisplayList || [])
		)
	}

	ngOnInit(): void {
		this.policyStartDate.valueChanges.pipe(takeUntil(this.#ngUnsubscribe$)).subscribe(async (d) => {
			try {
				await firstValueFrom(
					this.store.dispatch(new QuoteSessionAction.Quote.Update({ policyStartDate: d }))
				)
				await firstValueFrom(this.store.dispatch(new QuoteSessionAction.ActivatePolicyQuote()))
				await firstValueFrom(this.store.dispatch(new QuoteFlowAction.Update(true)))

				return
			} catch (err) {
				return Promise.reject(err)
			}
		})

		this.paymentFrequency.valueChanges
			.pipe(takeUntil(this.#ngUnsubscribe$))
			.subscribe(async (f) => {
				await this.#handlePaymentFrequencyChanges(f).catch((err) => Promise.reject(err))
			})
	}

	async #handlePaymentFrequencyChanges(paymentModality: PaymentFrequency): Promise<void> {
		try {
			const { pets } = await firstValueFrom(this.store.select<PetStateModel>(PetState))
			await firstValueFrom(
				this.store.dispatch(new QuoteSessionAction.Quote.Update({ paymentModality }))
			)
			// ? can this be removed
			if (pets.length && pets.some((p) => p.petCoverage !== undefined)) {
				await firstValueFrom(this.store.dispatch(new QuoteSessionAction.ActivatePolicyQuote()))
			}

			return
		} catch (err) {
			return Promise.reject(err)
		}
	}

	#setDateLimits(): void {
		const today = new Date()
		let minDate: Date = new Date()
		let maxDate: Date = new Date()
		minDate.setDate(today.getDate() + 1)
		maxDate.setDate(today.getDate() + 30)
		this.minDate = minDate
		this.maxDate = maxDate
	}

	ngOnDestroy(): void {
		this.#ngUnsubscribe$.next()
		this.#ngUnsubscribe$.unsubscribe()
	}
}
